// src/utils/quoteReporting.js
// QUOTE SEAM 12a VERIFIER: qreport-5b8e2c64
//
// THE estimates report, as pure functions. No Firestore, no React — the view
// (src/views/reports/EstimatesReportView.js) hands in what it has loaded and
// renders what comes back. functions/scripts/testQuoteReporting.js evaluates
// this file offline against the real web quoteSchema.js.
//
// WHERE EACH NUMBER COMES FROM (and why — see BACKLOG Seam 12a):
//   * Win rate      — the estimate's CURRENT stage outcome (resolveStageOutcome).
//                     Every estimate has a stage (backfilled), so this is honest
//                     for all history. Grouped by SEND date (sentAt, else
//                     createdAt) so it can never exceed 100%: won is always a
//                     subset of won + lost. Deferred and still-open estimates are
//                     shown beside it, never in the denominator.
//   * Open value    — the board's own rule (groupQuotesByStage, also called by
//                     QuotePipelineBoard), so the two can never disagree.
//   * Reasons, price feedback, days to close — quoteCloses ONLY. That record
//                     exists since Seam 9a (30 Sep 2026) with no backfill, holds
//                     the LATEST close per estimate (a Lost → Won estimate counts
//                     once, as won), and keeps its doc when reopened (reopenedAt)
//                     — a reopened estimate is open again and is left out.
//   * Never the activity log: no collection-group read is allowed on it.
//
// CONSULTANT SCOPING LIVES HERE, NOT IN THE SCREEN. resolveReportViewer turns
// the signed-in profile into either full access or "one salesperson only", and
// buildEstimatesReport narrows the estimates BEFORE computing anything, so no
// section — and no CSV built from the result — can carry another rep's
// figures. DISPLAY-LEVEL ONLY: firestore.rules already lets any org member read
// quotes and quoteCloses (logged in BACKLOG, not changed by this seam).
//
// No person's name ever enters the result. closedBy / detailsBy are not read.
// The view maps salesperson ids to names, and only in full-access mode.

import {
    resolveStageOutcome,
    resolveQuoteSalesPerson,
    toExpiryDate,
    ymdInTimeZone,
    normalizeStatus,
    quoteFollowUpOverdueDays,
    QUOTE_STATUSES,
    PIPELINE_STAGE_TYPES,
    PIPELINE_SYSTEM_STAGE_IDS,
    isQuoteArchived, // estdel-f9ac8ba8
} from './quoteSchema';

// Fewer closes than this in the range → counts only, no percentages or
// averages ("Not enough closed estimates yet").
export const REPORT_MIN_SAMPLE = 10;

// The day quoteCloses started being written (Seam 9a deploy). Nothing before it.
export const CLOSE_DATA_SINCE_YMD = '2026-09-30';

// Roles that see every salesperson. Mirrors ALL_ADMIN_ROLES in
// ProtectedCrmLayout.js — the same roles that open every other report.
export const ESTIMATES_REPORT_FULL_ROLES = Object.freeze([
    'Admin', 'Manager', 'Owner/System Admin', 'Owner / System Admin', 'Owner', 'System Admin',
]);
// Roles that see the report narrowed to estimates where they are the salesperson.
export const ESTIMATES_REPORT_SCOPED_ROLES = Object.freeze(['Sales Consultant']);

export const UNASSIGNED_SALESPERSON = '__unassigned__';

// ─── the board's value rule ─────────────────────────────────────────────────

// One estimate's value on the board: its total, or 0 when that is not a number.
export function quoteValue(quote) {
    const v = Number(quote && quote.total);
    return Number.isFinite(v) ? v : 0;
}

export function sumQuoteValue(list) {
    return (list || []).reduce((n, q) => n + quoteValue(q), 0);
}

// The value of a WON estimate: what the customer approved when we know it.
// Same precedence as the portal (approvedTotal ?? total).
export function wonQuoteValue(quote) {
    const a = Number(quote && quote.approvedTotal);
    return quote && quote.approvedTotal !== null && quote.approvedTotal !== undefined && Number.isFinite(a)
        ? a
        : quoteValue(quote);
}

/**
 * Estimates bucketed into the org's stages, in stage order — THE board rule.
 * QuotePipelineBoard calls this with its optimistic stageIdOf; the report calls
 * it with the stored pipelineStageId. Tolerates stages not loaded yet (null).
 *
 * @returns { columns: [{ stage, cards, count, total }], unplaced, boardTotal }
 */
export function groupQuotesByStage(quotes, stages, stageIdOf) {
    const list = Array.isArray(quotes) ? quotes : [];
    if (!Array.isArray(stages)) {
        return { columns: [], unplaced: { cards: [], count: 0, total: 0 }, boardTotal: 0 };
    }
    const idOf = typeof stageIdOf === 'function' ? stageIdOf : (q) => (q.pipelineStageId || null);
    const buckets = new Map();
    for (const s of stages) buckets.set(s.id, []);
    const loose = [];

    for (const q of list) {
        const sid = idOf(q);
        if (sid !== null && sid !== undefined && buckets.has(sid)) buckets.get(sid).push(q);
        else loose.push(q);   // no stage, or a stage this org no longer has
    }

    const columns = stages.map((s) => {
        const cards = buckets.get(s.id) || [];
        return { stage: s, cards, count: cards.length, total: sumQuoteValue(cards) };
    });

    return {
        columns,
        unplaced: { cards: loose, count: loose.length, total: sumQuoteValue(loose) },
        boardTotal: sumQuoteValue(list),
    };
}

// ─── who is looking ─────────────────────────────────────────────────────────

// FAIL CLOSED: an unknown role is denied, and a consultant with no id gets a
// scope that matches nothing — never the whole org.
export function resolveReportViewer(profile) {
    const role = profile && profile.role;
    if (ESTIMATES_REPORT_FULL_ROLES.includes(role)) return { access: 'full', salesPersonId: null };
    if (ESTIMATES_REPORT_SCOPED_ROLES.includes(role)) {
        const id = profile && typeof profile.id === 'string' && profile.id ? profile.id : null;
        return { access: 'scoped', salesPersonId: id };
    }
    return { access: 'denied', salesPersonId: null };
}

// ─── dates (company time zone) ──────────────────────────────────────────────

const pad2 = (n) => String(n).padStart(2, '0');
const ymd = (y, m, d) => `${y}-${pad2(m)}-${pad2(d)}`;
const lastDayOfMonth = (y, m) => new Date(Date.UTC(y, m, 0)).getUTCDate(); // m is 1-12

// Move a 'YYYY-MM' month by `delta` months. Returns [year, month1to12].
function shiftMonth(y, m, delta) {
    const idx = y * 12 + (m - 1) + delta;
    return [Math.floor(idx / 12), (idx % 12) + 1];
}

export const REPORT_RANGE_PRESETS = Object.freeze([
    { id: 'this_month', label: 'This month' },
    { id: 'last_month', label: 'Last month' },
    { id: 'last_3', label: 'Last 3 months' },
    { id: 'last_6', label: 'Last 6 months' },
    { id: 'last_12', label: 'Last 12 months' },
    { id: 'ytd', label: 'Year to date' },
    { id: 'all', label: 'All time' },
    { id: 'custom', label: 'Custom' },
]);
export const DEFAULT_REPORT_RANGE = 'last_3';

/**
 * A preset → inclusive { startYmd, endYmd } calendar days, from TODAY in the
 * company time zone (pass ymdInTimeZone(new Date(), tz)). "Last 3 months" is
 * this month plus the two before it, whole months. null bounds = open-ended.
 */
export function resolveReportRange(presetId, todayYmd, custom) {
    const [y, m] = String(todayYmd).split('-').map(Number);
    const monthStart = (yy, mm) => ymd(yy, mm, 1);
    const monthEnd = (yy, mm) => ymd(yy, mm, lastDayOfMonth(yy, mm));
    const back = (n) => { const [sy, sm] = shiftMonth(y, m, -(n - 1)); return { startYmd: monthStart(sy, sm), endYmd: todayYmd }; };
    switch (presetId) {
        case 'this_month': return { startYmd: monthStart(y, m), endYmd: todayYmd };
        case 'last_month': { const [py, pm] = shiftMonth(y, m, -1); return { startYmd: monthStart(py, pm), endYmd: monthEnd(py, pm) }; }
        case 'last_3': return back(3);
        case 'last_6': return back(6);
        case 'last_12': return back(12);
        case 'ytd': return { startYmd: ymd(y, 1, 1), endYmd: todayYmd };
        case 'all': return { startYmd: null, endYmd: null };
        case 'custom': {
            const ok = (s) => typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s);
            const s = custom && ok(custom.startYmd) ? custom.startYmd : null;
            const e = custom && ok(custom.endYmd) ? custom.endYmd : null;
            if (s && e && s > e) return { startYmd: e, endYmd: s };
            return { startYmd: s, endYmd: e };
        }
        default: return back(3);
    }
}

const inRange = (dayYmd, range) => !!dayYmd
    && (!range || !range.startYmd || dayYmd >= range.startYmd)
    && (!range || !range.endYmd || dayYmd <= range.endYmd);

// The day an estimate joins a win-rate group: when it was (last) sent, else
// when it was created. sentAt is re-stamped on every send, so a resend moves
// an estimate into a later month — noted on screen.
export function quoteCohortYmd(quote, timeZone) {
    const at = toExpiryDate(quote && quote.sentAt) || toExpiryDate(quote && quote.createdAt);
    return at ? ymdInTimeZone(at, timeZone) : null;
}

// ─── the report ─────────────────────────────────────────────────────────────

const emptyTally = () => ({ won: 0, lost: 0, deferred: 0, open: 0, wonValue: 0, lostValue: 0 });

function addToTally(t, outcome, quote) {
    if (outcome === PIPELINE_STAGE_TYPES.WON) { t.won += 1; t.wonValue += wonQuoteValue(quote); }
    else if (outcome === PIPELINE_STAGE_TYPES.LOST) { t.lost += 1; t.lostValue += quoteValue(quote); }
    else if (outcome === PIPELINE_STAGE_TYPES.DEFERRED) t.deferred += 1;
    else t.open += 1;   // open stage, or no known stage — not decided
}

function finishTally(t) {
    const decided = t.won + t.lost;
    return {
        ...t,
        decided,
        total: decided + t.deferred + t.open,
        winRate: decided > 0 ? t.won / decided : null,
    };
}

const average = (nums) => {
    const ok = nums.filter((n) => typeof n === 'number' && Number.isFinite(n));
    return ok.length ? { avg: ok.reduce((a, b) => a + b, 0) / ok.length, n: ok.length } : { avg: null, n: 0 };
};

export const NO_REASON = 'No reason given';
export const SKIPPED_REASON = 'Skipped';
export const NO_PRICE_FEEDBACK = 'No answer';

/**
 * Everything the Estimates report shows.
 *
 * @param input {
 *   quotes:  quote docs ({ id, ...data }), the whole org collection
 *   closes:  quoteCloses docs ({ id, ...data })
 *   stages:  resolvePipelineStages(...) — or null while loading
 *   timeZone: the company time zone (org doc `timezone`)
 *   range:   { startYmd, endYmd } from resolveReportRange
 *   viewer:  resolveReportViewer(profile)
 *   salesPersonFilter: a salesperson id, UNASSIGNED_SALESPERSON, or null = all.
 *                      IGNORED unless viewer.access === 'full'.
 *   now:     optional Date (tests)
 * }
 */
export function buildEstimatesReport(input) {
    const i = input || {};
    const viewer = i.viewer || { access: 'denied', salesPersonId: null };
    const stages = Array.isArray(i.stages) ? i.stages : null;
    const tz = i.timeZone;
    const range = i.range || { startYmd: null, endYmd: null };
    const now = toExpiryDate(i.now) || new Date();

    // 1. WHO — narrow first, compute second.
    // estdel-f9ac8ba8 — a deleted (hidden) estimate is out of every number; its close record drops with it
    // (closes join to a visible estimate below).
    const all = Array.isArray(i.quotes) ? i.quotes.filter((q) => q && q.id && !isQuoteArchived(q)) : [];
    let quotes;
    if (viewer.access === 'full') {
        const f = i.salesPersonFilter;
        quotes = !f ? all : all.filter((q) => {
            const sp = resolveQuoteSalesPerson(q);
            return f === UNASSIGNED_SALESPERSON ? !sp : sp === f;
        });
    } else if (viewer.access === 'scoped' && viewer.salesPersonId) {
        quotes = all.filter((q) => resolveQuoteSalesPerson(q) === viewer.salesPersonId);
    } else {
        quotes = [];
    }
    const quoteById = new Map(quotes.map((q) => [q.id, q]));
    const outcomeOf = (q) => (stages ? resolveStageOutcome(q.pipelineStageId, stages) : null);

    // 2. WIN RATE — by send date, current stage outcome, drafts left out.
    const cohort = quotes.filter((q) => q.pipelineStageId !== PIPELINE_SYSTEM_STAGE_IDS.DRAFT
        && inRange(quoteCohortYmd(q, tz), range));
    const overall = emptyTally();
    const byMonth = new Map();
    const bySp = new Map();
    for (const q of cohort) {
        const o = outcomeOf(q);
        addToTally(overall, o, q);
        const month = quoteCohortYmd(q, tz).slice(0, 7);
        if (!byMonth.has(month)) byMonth.set(month, emptyTally());
        addToTally(byMonth.get(month), o, q);
        if (viewer.access === 'full') {
            const sp = resolveQuoteSalesPerson(q) || UNASSIGNED_SALESPERSON;
            if (!bySp.has(sp)) bySp.set(sp, emptyTally());
            addToTally(bySp.get(sp), o, q);
        }
    }
    const winRate = finishTally(overall);
    const months = [...byMonth.entries()].sort((a, b) => (a[0] < b[0] ? -1 : 1))
        .map(([month, t]) => ({ month, ...finishTally(t) }));
    const salespeople = viewer.access === 'full'
        ? [...bySp.entries()].map(([salesPersonId, t]) => ({ salesPersonId, ...finishTally(t) }))
            .sort((a, b) => b.total - a.total)
        : null;

    // 3. OPEN VALUE — right now (no date range), the board's rule, open stages only.
    const grouped = groupQuotesByStage(quotes, stages);
    const openColumns = grouped.columns.filter((c) => c.stage.type === PIPELINE_STAGE_TYPES.OPEN);
    const openValue = {
        stages: openColumns.map((c) => ({ stageId: c.stage.id, label: c.stage.label, count: c.count, total: c.total })),
        count: openColumns.reduce((n, c) => n + c.count, 0),
        total: openColumns.reduce((n, c) => n + c.total, 0),
        draftTotal: (openColumns.find((c) => c.stage.id === PIPELINE_SYSTEM_STAGE_IDS.DRAFT) || { total: 0 }).total,
        unplacedCount: grouped.unplaced.count,
        loaded: !!stages,
    };

    // 4. FOLLOW-UPS — right now. Same exclusions as the Needs-attention rule:
    //    decided estimates and Lost/Deferred stages are not chases.
    const todayYmd = ymdInTimeZone(now, tz);
    let overdue = 0;
    let dueToday = 0;
    for (const q of quotes) {
        if (q.convertedToJobId) continue;
        const st = normalizeStatus(q.status);
        if (st === QUOTE_STATUSES.APPROVED || st === QUOTE_STATUSES.DECLINED) continue;
        const o = outcomeOf(q);
        if (o === PIPELINE_STAGE_TYPES.LOST || o === PIPELINE_STAGE_TYPES.DEFERRED) continue;
        if (quoteFollowUpOverdueDays(q, { now, timeZone: tz }) !== null) { overdue += 1; continue; }
        const nextAt = toExpiryDate(q.nextFollowUpAt);
        const snoozedUntil = toExpiryDate(q.snoozedUntil);
        const snoozed = snoozedUntil && snoozedUntil.getTime() > now.getTime();
        if (nextAt && !snoozed && ymdInTimeZone(nextAt, tz) === todayYmd) dueToday += 1;
    }

    // 5. CLOSES — quoteCloses in the range, not reopened, joined to a VISIBLE
    //    estimate (that join is what carries the scope and the filter).
    const closes = (Array.isArray(i.closes) ? i.closes : []).filter((c) => {
        if (!c || c.reopenedAt) return false;
        const qid = c.quoteId || c.id;
        if (!quoteById.has(qid)) return false;
        const at = toExpiryDate(c.closedAt);
        return !!at && inRange(ymdInTimeZone(at, tz), range);
    });

    // ONE set of stage columns for the Won AND Lost tables — every stage any
    // in-range won/lost close came from, in pipeline order, unknown last — so
    // the two tables line up column for column (founder round 2026-10-02).
    const stageOrder = new Map((stages || []).map((s, idx) => [s.id, idx]));
    const reasonStageIds = [...new Set(closes
        .filter((c) => c.outcome === 'won' || c.outcome === 'lost')
        .map((c) => c.fromStageId || 'unknown'))]
        .sort((a, b) => (stageOrder.has(a) ? stageOrder.get(a) : 999)
            - (stageOrder.has(b) ? stageOrder.get(b) : 999));
    const reasonTable = (outcome) => {
        const rows = new Map();
        for (const c of closes) {
            if (c.outcome !== outcome) continue;
            const reason = c.reason || (c.nudgeDismissedAt ? SKIPPED_REASON : NO_REASON);
            const col = c.fromStageId || 'unknown';
            if (!rows.has(reason)) rows.set(reason, { reason, counts: {}, total: 0 });
            const r = rows.get(reason);
            r.counts[col] = (r.counts[col] || 0) + 1;
            r.total += 1;
        }
        const stageIds = reasonStageIds;
        // Answered reasons by count; the two "no answer" rows always last.
        const last = (r) => (r.reason === NO_REASON || r.reason === SKIPPED_REASON ? 1 : 0);
        const list = [...rows.values()].sort((a, b) => last(a) - last(b) || b.total - a.total || a.reason.localeCompare(b.reason));
        return { stageIds, rows: list };
    };

    const pf = new Map();
    for (const c of closes) {
        const key = c.priceFeedback || NO_PRICE_FEEDBACK;
        if (!pf.has(key)) pf.set(key, { value: key, won: 0, lost: 0, deferred: 0, total: 0 });
        const r = pf.get(key);
        if (c.outcome === 'won' || c.outcome === 'lost' || c.outcome === 'deferred') r[c.outcome] += 1;
        r.total += 1;
    }
    const priceFeedback = [...pf.values()].sort((a, b) => (a.value === NO_PRICE_FEEDBACK) - (b.value === NO_PRICE_FEEDBACK)
        || b.total - a.total || a.value.localeCompare(b.value));

    const days = (outcome) => {
        const list = closes.filter((c) => c.outcome === outcome);
        return {
            total: average(list.map((c) => c.totalDaysOpen)),
            active: average(list.map((c) => c.activeDaysOpen)),
        };
    };

    // Decided estimates in the win-rate group with no close record — they
    // closed before Seam 9a, so their reason was never asked.
    const closedIds = new Set((Array.isArray(i.closes) ? i.closes : []).filter((c) => c && !c.reopenedAt).map((c) => c.quoteId || c.id));
    const decidedWithoutDetails = cohort.filter((q) => {
        const o = outcomeOf(q);
        return (o === PIPELINE_STAGE_TYPES.WON || o === PIPELINE_STAGE_TYPES.LOST || o === PIPELINE_STAGE_TYPES.DEFERRED)
            && !closedIds.has(q.id);
    }).length;

    const closeCounts = { won: 0, lost: 0, deferred: 0 };
    for (const c of closes) if (closeCounts[c.outcome] !== undefined) closeCounts[c.outcome] += 1;
    const answered = closes.filter((c) => c.reason || c.priceFeedback).length;

    return {
        access: viewer.access,
        scoped: viewer.access === 'scoped',
        range,
        estimateCount: quotes.length,
        winRate,
        months,
        salespeople,
        openValue,
        followUps: { overdue, dueToday },
        closes: {
            count: closes.length,
            ...closeCounts,
            answered,
            sampleOk: closes.length >= REPORT_MIN_SAMPLE,
            rangeStartsBeforeData: !range.startYmd || range.startYmd < CLOSE_DATA_SINCE_YMD,
            decidedWithoutDetails,
            reasonStageIds,
            reasonsWon: reasonTable('won'),
            reasonsLost: reasonTable('lost'),
            priceFeedback,
            daysWon: days('won'),
            daysLost: days('lost'),
        },
    };
}
